import React, { useCallback } from 'react';
import { useForm, useField } from 'react-final-form-hooks';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import MuiTextField from '../_common/TextField';
import GoalCalenderIndicator from './GoalCalenderIndicator';
import { GoalItemProps } from './GoalItemContainer';
import { requiredPositiveInteger } from 'helpers/validationRules';
import { determineUnitToShow } from 'helpers/conversions';
import { styles } from './GoalItem.styles';

const GoalItem = (props: GoalItemProps) => {
  const { t } = useTranslation();
  const {
    goalTypeStr,
    goalCurrentVal,
    goalImpVal,
    lastUpdateNote,
    goalEntry,
    passedDateIndicator,
    userSettings,
    selectedSystemGroup,
    updateGoal,
  } = props;

  const validate = useCallback(
    values => ({
      goals: requiredPositiveInteger(values.goals),
    }),
    []
  );

  const onSubmit = useCallback(
    values => {
      updateGoal(goalEntry.goalType, values.goals, selectedSystemGroup);
      values.goals = '';
    },
    [updateGoal, goalEntry, selectedSystemGroup]
  );

  const { form, handleSubmit } = useForm({
    onSubmit,
    validate,
  });

  const goalsInput = useField('goals', form);

  return (
    <Paper sx={styles.goal}>
      <Box sx={styles.goalHeader}>
        <Typography component="h3" sx={styles.goalHeaderTitle}>
          {t(goalTypeStr)}
        </Typography>
      </Box>

      <Grid container sx={styles.goalBody}>
        <Grid size={{ xs: 12, md: 6 }} sx={styles.goalMain}>
          <Typography component="h3" sx={styles.goalName}>
            {t('goals.myGoal')}
          </Typography>
          <Box sx={styles.goalInfoWrapper}>
            <Box sx={styles.goalValue}>
              <Box component="h1" data-automationid={`${goalTypeStr}Value`} sx={styles.goalValueH1}>
                {goalCurrentVal}
              </Box>
              <Box component="span" sx={styles.goalValueSpan}>
                {determineUnitToShow(selectedSystemGroup, userSettings, t)}
              </Box>
            </Box>
            <Box>
              <form noValidate autoComplete="off" onSubmit={handleSubmit}>
                <Box sx={styles.formWrapper}>
                  <MuiTextField
                    data-automationid={`${goalTypeStr} setNewGoalField`}
                    {...goalsInput.input}
                    type="number"
                    error={goalsInput.meta.touched && goalsInput.meta.error}
                    name="newGoal"
                    variant="outlined"
                    placeholder={t('goals.setNewGoal')}
                    sx={styles.textField}
                  />
                  <Fab
                    type="submit"
                    disabled={!!goalsInput.meta.error || !goalsInput.input.value}
                    sx={styles.formButton}>
                    <span>{t('goals.set')}</span>
                  </Fab>
                </Box>
              </form>
            </Box>
          </Box>
          <Typography component="h4" sx={styles.formattedDate}>
            {lastUpdateNote}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} sx={styles.impressions}>
          <div>
            <Typography component="h3" sx={styles.goalName}>
              {t('goals.currentImpressions')}
            </Typography>
            <Box sx={styles.currentValue}>
              <Box
                component="h1"
                data-automationid={`${goalTypeStr}CurrentProductionValue`}
                sx={styles.goalValueH1}>
                {goalImpVal}
              </Box>
              <Box component="span" sx={styles.goalValueSpan}>
                {determineUnitToShow(selectedSystemGroup, userSettings, t)}
              </Box>
            </Box>
            <GoalCalenderIndicator
              key={goalEntry.goalType}
              goalId={goalEntry.goalType}
              passedDateIndicator={passedDateIndicator}
            />
          </div>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default GoalItem;
